import React from 'react'
import styled, { css } from 'styled-components'
import { useStore } from '../zustandStore'

import WithForcedMonospace from './WithForcedMonospace'
import WithAnimatedDeltas from './WithAnimatedDeltas'
import WithPodiumStyles from './WithPodiumStyles'

import BingoPointsCell from './BingoPointsCell'

import { NO_INFORMATION_SYMBOL, CHECK_MARK } from '../unicodeChars'
import { trimMs, getChapterProgressTooltipText, CHAPTER_IN_PROGRESS } from '../utils' 

const PlayerTotalAnimatedDelta =({
  player,
  withPodiumStyles,
  omitDeltaForChapterNames = [],
  className = '',
  deltasClassName = '',
  isStandalone,
  timeSpacerString = '',
  animatedDeltaProps = {},
  msDecimals = 3,
  prependBingoPoints,
  // this is toggled back and forth by a setTimeout in ObsPlayerTotal to switch it back and forth when appropriate
  forceDisplayBingoPoints = false
}) => {
  const {
    event,
    chapters,
    layoutSettings
  } = useStore(state => ({
    event: state.event,
    chapters: state.event.chapters,
    layoutSettings: state.layoutSettings
  }))


  const mostRecentUpdatedAt = Math.max(...Object.values(player.eventBestTimesUpdatedAt))
  let mostRecentChapter, mostRecentDelta
  if (mostRecentUpdatedAt) {
    mostRecentChapter = Object.keys(player.eventBestTimesUpdatedAt)
                              .find(key => player.eventBestTimesUpdatedAt[key] === mostRecentUpdatedAt)

    mostRecentDelta = player.eventChapterDeltas[mostRecentChapter]
  }

  // the wrapper span on this output makes the smaller decimals vertically align AND I HAVE NO IDEA WHY
  const TotalTimeComponent = () => (
    <span className={`${isStandalone || event.isFullGame ? '' : 'total-color'}`}>
      {withPodiumStyles
        ? <WithPodiumStyles rank={player.rank}>{trimMs(player.eventBestTimes.total, msDecimals)}</WithPodiumStyles>
        : <WithForcedMonospace>{trimMs(player.eventBestTimes.total, msDecimals)}</WithForcedMonospace>
      }
    </span>
  )

  const chapterProgressTooltipText = isStandalone 
    ? ''
    : getChapterProgressTooltipText(player, event, layoutSettings)

  const isCurrentBingoChapterInProgress = event.isBingo && (player.bingoChapterStates[player.bingoCurrentChapter] === CHAPTER_IN_PROGRESS)

  const displayPrependedBingoPoints = 
    (
      event.isBingo && prependBingoPoints && // the rendering component needs to enable this, and it only works in bingo mode
      !isStandalone &&                       // this is redundant as the rendering component needs to pass a prop to enable but can't hurt
      player.completedAnyChapters &&         // you need a total time to show
      isCurrentBingoChapterInProgress        // and you are still working on your present chapter (so points is an interesting number)
    )
  
  return (
    <Wrapper isStandalone={isStandalone} className={className}>
      {timeSpacerString && (
        <GridCell invisible>
          <WithAnimatedDeltas>
            <WithForcedMonospace>
              {timeSpacerString}
            </WithForcedMonospace>
          </WithAnimatedDeltas>
        </GridCell>
      )}

      {displayPrependedBingoPoints && (
        <BingoPointsCell
          player={player}
          chapter={chapters[player.bingoCurrentChapter]}
          isPrepended
        />
      )}

      <GridCell expandLeft={displayPrependedBingoPoints}>
        {player.completedAllChapters || (event.isBingo && player.completedAnyChapters)
          ? ( 
            // are we showing a time here? in full_game and il modes, 
            // that's "did you finish all the chapters (or the only chapter)"
            // in bingo mode, that's "did you finish at least one chapter"
            <>
              {event.isBingo && isStandalone && isCurrentBingoChapterInProgress && forceDisplayBingoPoints ? (
                // this is how forceDisplayBingoPoints in combination with setTimeout 
                // makes this component switch back and forth, in the standalone total
                <BingoPointsCell
                  player={player}
                  chapter={chapters[player.bingoCurrentChapter]}
                />
              ) : (
                <>
                  {!event.isBingo && mostRecentDelta && mostRecentDelta[0] === '-' && !omitDeltaForChapterNames.includes(mostRecentChapter) ? (
                    <WithAnimatedDeltas
                      delta={mostRecentDelta}
                      chapterName={mostRecentChapter}
                      updatedAt={mostRecentUpdatedAt}
                      bestTime={player.eventBestTimes.total}
                      monospaceWidthEm={0.5}
                      className={deltasClassName}
                      isFullGame={event.isFullGame}
                      {...animatedDeltaProps}
                    >
                      <TotalTimeComponent />
                    </WithAnimatedDeltas>
                  ) : (
                    <TotalTimeComponent />
                  )}
                  {event.isBingo && (
                    <BingoChapterProgressSuperscript className='superscript' isStandalone={isStandalone}>
                      {/* the open and close parens here are plain text, enclosing the superscript. the syntax parser i'm seeing now is defeated by this notion */}
                      (
                      <span>
                        {player.completedAllChapters ? CHECK_MARK : player.completedChapterCount}
                      </span>
                      )
                    </BingoChapterProgressSuperscript>
                  )}
                </>
              )}
            </>
          ) : (
            // the first ternary above has come back false: there's no time information to display
            event.isFullGame ? (
              // in full_game mode, we show a -- in the rows of the leaderboard and nothing in the standalone source
              isStandalone ? (
                null
              ) : (
                <WithInvisibleSpacer hyphen>
                  {NO_INFORMATION_SYMBOL}
                </WithInvisibleSpacer>
              )
            ) : event.isBingo ? (
              // in bingo mode, before the player has finished the first chapter
              // we always show the number of points they have on the first board
              <BingoPointsCell
                className={isStandalone ? '' : 'scoot-left'}
                player={player}
                chapter={chapters[player.bingoCurrentChapter]}
              />
            ) : (
              // in IL mode, the number of completed chapters goes here in every context:
              // standalone playertotal source and simple board row
              <WithInvisibleSpacer>
                <ChapterProgress title={chapterProgressTooltipText} isStandalone={isStandalone}>
                  <WithForcedMonospace>
                    {`${player.completedChapterCount || 0}/${event.chapterNames.length}`}
                  </WithForcedMonospace>
                  &nbsp;complete
                </ChapterProgress>
              </WithInvisibleSpacer>
            )
          )
        }
      </GridCell>
    </Wrapper>
  )
}

export default PlayerTotalAnimatedDelta

const Wrapper = styled.div`
  line-height: 1.29em;
  ${props => props.isStandalone ? css`
    display: grid !important;
  ` : css`
    display: flex !important;
  `}

  .total-color {
    color: var(--total-color);
  }

  .scoot-left {
    margin-right: 0.2em;
  }
`

const ChapterProgress = styled.div`
  font-size: 68%;
  ${props => props.isStandalone ? css`
    text-align: center;
  ` : css`
    color: var(--completed-chapter-count-color);
  `}
`

const BingoChapterProgressSuperscript = styled.sup`
  > span {
    display: inline-block;
    min-width: 0.75em;
    width: 0.75em;
  }
  margin-left: 0.2em;
  margin-right: 0.15em;
  font-size: 55%;
  line-height: normal;
  margin-top: 0.2em;
  color: var(--completed-chapter-count-color);
`
const DisplayGrid = styled.div`
  display: grid;
`
const GridCell = styled.div`
  grid-column: 1;
  grid-row: 1;
  white-space: nowrap;
  /* commenting this out might break bingo styling in prod only? */
  /* span {
    display: inline-block;
  } */
  text-align: ${props => props.textAlign || 'center'};
  ${props => props.invisible && css`
    visibility: hidden;
  `}
  ${props => props.hyphen && css`
    padding-right: 0.1em;
    /* text-align: center; */
  `}

  /* this spaces out the prepended bingo points by making the time take up empty space */
  ${props => props.expandLeft && css`
    min-width: 4.05em;
    display: flex;
    justify-content: flex-end;
  `}
`

const WithInvisibleSpacer = ({children, hyphen}) => (
  <DisplayGrid>
    <GridCell textAlign='right' hyphen={hyphen}>
      {children}
    </GridCell>
    <GridCell invisible>
      <WithForcedMonospace>
        00:00.000
      </WithForcedMonospace>
    </GridCell>
  </DisplayGrid>
)